import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  AlertIcon,
  Box,
  HStack,
  Spinner,
  Stack,
  useToast,
} from "@chakra-ui/react";
import { BookOpen, BarChart3, Pencil } from "lucide-react";
import { useParams } from "react-router-dom";
import OrganizationDetailHeader from "../organization/OrganizationDetailHeader";
import OrganizationInfoSection from "../organization/OrganizationInfoSection";
import OrganizationBillingSection from "../organization/OrganizationBillingSection";
import OrganizationNotesSection from "../organization/OrganizationNotesSection";
import OrganizationRelationshipsSection from "../organization/OrganizationRelationshipsSection";
import { fetchWorkbookAvailability } from "../organization/organizationSurveysApi";
import { useUser } from "../../contexts/UserContext";
import { canManageNetwork } from "../../utils/authorization";
import { getNetwork, getNetworkOrganization, getNetworkOrganizations, getNetworkOrganizationUsers } from "./networkApi";

const uniqueUsers = (users = []) => {
  const seen = new Set();
  return users.filter((user, index) => {
    if (!user) return false;
    const key = user?.id != null ? `id:${user.id}` : user?.email ? `email:${user.email}` : `idx:${index}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const NetworkOrganizationView = () => {
  const { networkId, organizationId } = useParams();
  const { user } = useUser();
  const toast = useToast();

  const [network, setNetwork] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [hasWorkbook, setHasWorkbook] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadOrganization = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [networkPayload, networkOrganizationsPayload] = await Promise.all([
        getNetwork(networkId),
        getNetworkOrganizations(networkId),
      ]);

      const scopedOrganization = (networkOrganizationsPayload || []).find(
        (entry) => String(entry?.id) === String(organizationId)
      );

      if (!scopedOrganization) {
        setError("This school is not available in the active network.");
        setOrganization(null);
        setNetwork(networkPayload);
        return;
      }

      const [organizationPayload, organizationUsersPayload, workbookAvailable] = await Promise.all([
        getNetworkOrganization(networkId, organizationId),
        getNetworkOrganizationUsers(networkId, organizationId).catch(() => []),
        fetchWorkbookAvailability(organizationId).catch(() => false),
      ]);

      setNetwork(networkPayload);
      setOrganization({
        ...scopedOrganization,
        ...organizationPayload,
        users: uniqueUsers([
          ...(Array.isArray(scopedOrganization?.users) ? scopedOrganization.users : []),
          scopedOrganization?.user,
          ...(Array.isArray(organizationPayload?.users) ? organizationPayload.users : []),
          organizationPayload?.user,
          ...organizationUsersPayload,
        ]),
      });
      setHasWorkbook(workbookAvailable);
    } catch (err) {
      console.error("Error loading network organization:", err);
      setError("Could not load school.");
      toast({ title: "Could not load school", status: "error", duration: 4000, isClosable: true });
    } finally {
      setLoading(false);
    }
  }, [networkId, organizationId, toast]);

  useEffect(() => {
    loadOrganization();
  }, [loadOrganization]);

  const organizationNetworks = useMemo(() => {
    if (Array.isArray(organization?.networks) && organization.networks.length > 0) {
      return organization.networks;
    }
    return network ? [network] : [];
  }, [network, organization]);

  const organizationUsers = useMemo(
    () => Array.isArray(organization?.users) ? organization.users : [],
    [organization]
  );
  const canManageActiveNetwork = canManageNetwork(user, networkId);

  if (loading) {
    return <HStack justify="center" py={12}><Spinner size="lg" /></HStack>;
  }

  if (error) {
    return <Alert status="error" borderRadius="md"><AlertIcon />{error}</Alert>;
  }

  if (!organization) return null;

  return (
    <Box p={{ base: 4, md: 6 }} maxW="6xl" mx="auto">
      <OrganizationDetailHeader
        title={organization.name}
        subtitle={`Network school${network?.name ? ` · ${network.name}` : ""}`}
        actions={[
          { label: "Back to schools", to: `/app/network/${networkId}/organizations`, variant: "ghost" },
          ...(canManageActiveNetwork ? [{
            label: "Edit school",
            ariaLabel: "Edit school",
            tooltip: "Edit school",
            to: `/app/network/${networkId}/organizations/${organizationId}/edit`,
            leftIcon: <Pencil size={16} />,
            variant: "outline",
            colorScheme: "yellow",
          }] : []),
          {
            label: "Survey Results",
            to: `/app/network/${networkId}/results`,
            leftIcon: <BarChart3 size={16} />,
            variant: "outline",
            colorScheme: "green",
          },
          ...(hasWorkbook ? [{
            label: "View Workbook",
            to: `/app/network/${networkId}/organizations/${organizationId}/workbook`,
            leftIcon: <BookOpen size={16} />,
            variant: "outline",
            colorScheme: "purple",
          }] : []),
        ]}
      />

      <Stack spacing={4}>
        <OrganizationInfoSection organization={organization} />
        <OrganizationBillingSection organization={organization} />
        <OrganizationNotesSection notes={organization.notes} />
        <OrganizationRelationshipsSection
          users={organizationUsers}
          networks={organizationNetworks}
          getUserEditPath={canManageActiveNetwork ? (organizationUser) => `/app/network/${networkId}/users/edit/${organizationUser.id}` : undefined}
        />
      </Stack>
    </Box>
  );
};

export default NetworkOrganizationView;
